import axios from 'axios';
import { getTokens, saveTokens, clearTokens } from './authApi';

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api') + '/admin';

// Create axios instance with interceptors
const paymentApi = axios.create({
  baseURL: API_BASE_URL,
});

// Add request interceptor to include access token
paymentApi.interceptors.request.use(
  (config) => {
    const tokens = getTokens();
    if (tokens?.accessToken) {
      config.headers.Authorization = `Bearer ${tokens.accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor to handle token refresh
paymentApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const tokens = getTokens();
        if (tokens?.refreshToken) {
          // Refresh at the correct auth endpoint (NOT under /admin)
          const response = await axios.post('/api/auth/refresh', {
            refreshToken: tokens.refreshToken,
          });

          const { tokens: newTokens } = response.data;
          saveTokens(newTokens);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
          return paymentApi(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, clear tokens and redirect to login
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export interface Payment {
  id?: number;
  payment_number: string;
  entity_type: 'estimate' | 'work_order';
  entity_id: number;
  customer_id: number;
  amount: number;
  payment_method: 'card' | 'cash' | 'check' | 'affirm' | 'paypal' | 'other';
  payment_status: 'pending' | 'processing' | 'completed' | 'failed' | 'refunded' | 'partially_refunded';
  processor?: 'stripe' | 'affirm' | 'paypal' | 'manual';
  processor_payment_id?: string;
  processor_fee?: number;
  net_amount?: number;
  card_brand?: string; // e.g., "Visa", "Mastercard", "Amex", "Discover"
  card_last4?: string; // Last 4 digits of card number
  check_number?: string;
  reference_number?: string;
  notes?: string;
  recorded_by?: number;
  payment_date: string;
  refund_amount?: number;
  refund_reason?: string;
  refunded_at?: string;
  created_at?: string;
  updated_at?: string;
}

export interface PaymentPlan {
  id?: number;
  plan_number: string;
  entity_type: 'estimate' | 'work_order';
  entity_id: number;
  customer_id: number;
  total_amount: number;
  down_payment: number;
  financed_amount: number;
  number_of_installments: number;
  installment_amount: number;
  frequency: 'weekly' | 'biweekly' | 'monthly';
  apr_rate?: number;
  total_interest?: number;
  processor?: 'affirm' | 'manual';
  processor_plan_id?: string;
  credit_check_performed: boolean;
  credit_check_type?: 'soft' | 'hard' | 'none';
  credit_approved?: boolean;
  status: 'pending' | 'active' | 'completed' | 'defaulted' | 'cancelled';
  amount_paid: number;
  balance_remaining: number;
  next_payment_date?: string;
  created_by?: number;
  approved_by?: number;
  notes?: string;
  start_date: string;
  end_date?: string;
  completed_at?: string;
  cancelled_at?: string;
  cancellation_reason?: string;
  created_at?: string;
  updated_at?: string;
  installments?: PaymentPlanInstallment[];
}

export interface PaymentPlanInstallment {
  id?: number;
  payment_plan_id: number;
  payment_id?: number;
  installment_number: number;
  amount_due: number;
  amount_paid: number;
  status: 'pending' | 'paid' | 'late' | 'missed' | 'waived';
  due_date: string;
  paid_date?: string;
  days_late: number;
  late_fee: number;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

export interface PaymentSettings {
  id?: number;
  stripe_enabled: boolean;
  stripe_publishable_key?: string;
  stripe_secret_key?: string; // Only for sending to backend, never received
  stripe_webhook_secret?: string; // Only for sending to backend, never received
  affirm_enabled: boolean;
  paypal_enabled: boolean;
  accept_cash: boolean;
  accept_check: boolean;
  require_check_approval: boolean;
  allow_payment_plans: boolean;
  min_payment_plan_amount: number;
  max_payment_plan_months: number;
  require_down_payment: boolean;
  min_down_payment_percentage: number;
  send_payment_receipts: boolean;
  charge_late_fees: boolean;
  late_fee_amount: number;
  late_fee_grace_period_days: number;
  created_at?: string;
  updated_at?: string;
}

export interface PaymentSummary {
  total_amount: number;
  amount_paid: number;
  balance_due: number;
  payment_status: 'unpaid' | 'partial' | 'paid' | 'refunded' | 'payment_plan';
}

export interface InvoiceLinePart {
  id?: number;
  invoice_line_item_id: number;
  part_name: string;
  part_number?: string;
  quantity: number;
  unit_price: number;
  discount_percentage?: number;
  discount_amount?: number;
  discounted_price?: number;
  total_price: number;
  notes?: string;
  created_at?: string;
}

export interface InvoiceLineItem {
  id?: number;
  invoice_id: number;
  line_number: number;
  concern: string;
  cause?: string;
  correction?: string;
  labor_hours?: number;
  labor_rate?: number;
  labor_discount_percentage?: number;
  labor_discount_amount?: number;
  labor_total?: number;
  parts_total?: number;
  line_total: number;
  is_diagnosis: boolean;
  is_diagnosis_waived: boolean;
  diagnosis_waiver_amount?: number;
  original_line_total?: number;
  notes?: string;
  customer_notes?: string;
  created_at?: string;
  parts?: InvoiceLinePart[];
}

export interface Invoice {
  id?: number;
  invoice_number: string;
  work_order_id: number;
  customer_id: number;
  vehicle_id?: number;
  invoice_date: string;
  due_date?: string;
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  total_amount: number;
  amount_paid: number;
  payment_method?: string;
  payment_date?: string;
  payment_id?: number;
  payments?: Payment[]; // All payments for this work order (for split payment display)
  status: 'draft' | 'paid' | 'void' | 'refunded';
  is_archived: boolean;
  archived_at?: string;
  notes?: string;
  internal_notes?: string;
  is_tax_exempt?: boolean;
  tax_exempt_reason?: string | null;
  created_at?: string;
  updated_at?: string;
  line_items?: InvoiceLineItem[];
  customer?: any;
  vehicle?: any;
  signature_data?: string;
  signed_at?: string;
}

export interface ManualPaymentResponse {
  payment: Payment;
  invoice: Invoice | null;
  workflowTriggered: boolean;
}

class PaymentService {
  // Payment Settings
  async getSettings(): Promise<PaymentSettings> {
    const response = await paymentApi.get('/payments/settings');
    return response.data;
  }

  async updateSettings(settings: Partial<PaymentSettings>): Promise<PaymentSettings> {
    const response = await paymentApi.put('/payments/settings', settings);
    return response.data;
  }

  async getAvailablePaymentMethods(): Promise<string[]> {
    const response = await paymentApi.get('/payments/methods');
    return response.data.methods;
  }

  async getStripePublishableKey(): Promise<string> {
    const response = await paymentApi.get('/payments/stripe/publishable-key');
    return response.data.publishableKey;
  }

  // Payment Intents
  async createPaymentIntent(params: {
    amount: number;
    entityType: 'estimate' | 'work_order';
    entityId: number;
    customerId: number;
    customerEmail: string;
    description?: string;
  }): Promise<{ clientSecret: string; paymentIntentId: string }> {
    const response = await paymentApi.post('/payments/intent', params);
    return response.data;
  }

  async createAffirmPaymentIntent(params: {
    amount: number;
    entityType: 'estimate' | 'work_order';
    entityId: number;
    customerId: number;
    customerEmail: string;
    customerName: string;
    description?: string;
  }): Promise<{ clientSecret: string; paymentIntentId: string }> {
    const response = await paymentApi.post('/payments/affirm/intent', params);
    return response.data;
  }

  async createCheckoutSession(params: {
    amount: number;
    entityType: 'estimate' | 'work_order';
    entityId: number;
    customerId: number;
    customerEmail: string;
    customerName?: string;
    description?: string;
    successUrl?: string;
    cancelUrl?: string;
  }): Promise<{ sessionId: string; url: string }> {
    const response = await paymentApi.post('/payments/checkout-session', params);
    return response.data;
  }

  // Manual Payments
  async recordManualPayment(params: {
    amount: number;
    entityType: 'estimate' | 'work_order';
    entityId: number;
    customerId: number;
    paymentMethod: 'cash' | 'check' | 'other';
    checkNumber?: string;
    referenceNumber?: string;
    notes?: string;
  }): Promise<ManualPaymentResponse> {
    const response = await paymentApi.post('/payments/manual', params);
    return response.data;
  }

  // Payment Queries
  async getAllPayments(): Promise<Payment[]> {
    const response = await paymentApi.get('/payments');
    return response.data;
  }

  async getPaymentById(id: number): Promise<Payment> {
    const response = await paymentApi.get(`/payments/${id}`);
    return response.data;
  }

  async getPaymentsForEntity(
    entityType: 'estimate' | 'work_order',
    entityId: number
  ): Promise<{ payments: Payment[]; summary: PaymentSummary }> {
    const response = await paymentApi.get(`/payments/entity/${entityType}/${entityId}`);
    return response.data;
  }

  async getPaymentsForCustomer(customerId: number): Promise<Payment[]> {
    const response = await paymentApi.get(`/payments/customer/${customerId}`);
    return response.data;
  }

  async searchPayments(searchTerm: string): Promise<Payment[]> {
    const response = await paymentApi.get(`/payments/search/${searchTerm}`);
    return response.data;
  }

  // Refunds
  async processRefund(paymentId: number, amount?: number, reason?: string): Promise<Payment> {
    const response = await paymentApi.post(`/payments/${paymentId}/refund`, { amount, reason });
    return response.data;
  }

  async deletePayment(paymentId: number): Promise<void> {
    await paymentApi.delete(`/payments/${paymentId}`);
  }

  // Payment Plans
  async createPaymentPlan(params: {
    entityType: 'estimate' | 'work_order';
    entityId: number;
    customerId: number;
    totalAmount: number;
    downPayment?: number;
    numberOfInstallments: number;
    frequency?: 'weekly' | 'biweekly' | 'monthly';
    startDate?: string;
    notes?: string;
  }): Promise<PaymentPlan> {
    const response = await paymentApi.post('/payments/plans', params);
    return response.data;
  }

  async getAllPaymentPlans(): Promise<PaymentPlan[]> {
    const response = await paymentApi.get('/payments/plans');
    return response.data;
  }

  async getPaymentPlanById(id: number): Promise<PaymentPlan> {
    const response = await paymentApi.get(`/payments/plans/${id}`);
    return response.data;
  }

  async getPaymentPlanForEntity(
    entityType: 'estimate' | 'work_order',
    entityId: number
  ): Promise<PaymentPlan | null> {
    try {
      const response = await paymentApi.get(`/payments/plans/entity/${entityType}/${entityId}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async recordInstallmentPayment(
    planId: number,
    installmentId: number,
    paymentId: number,
    amountPaid: number
  ): Promise<PaymentPlan> {
    const response = await paymentApi.post(
      `/payments/plans/${planId}/installments/${installmentId}/pay`,
      { paymentId, amountPaid }
    );
    return response.data;
  }

  async cancelPaymentPlan(planId: number, reason: string): Promise<PaymentPlan> {
    const response = await paymentApi.post(`/payments/plans/${planId}/cancel`, { reason });
    return response.data;
  }

  async getUpcomingPayments(days: number = 7): Promise<PaymentPlan[]> {
    const response = await paymentApi.get(`/payments/plans/upcoming/${days}`);
    return response.data;
  }

  // Confirm payment after frontend confirmation (fallback for when webhooks don't work)
  async confirmPaymentIntent(paymentIntentId: string): Promise<{ success: boolean; status: string }> {
    const response = await paymentApi.post(`/payments/confirm/${paymentIntentId}`);
    return response.data;
  }

  // Confirm checkout session after payment link payment
  async confirmCheckoutSession(sessionId: string): Promise<{ success: boolean; status: string }> {
    const response = await paymentApi.post(`/payments/confirm-session/${sessionId}`);
    return response.data;
  }
}

export const paymentService = new PaymentService();
